import { Columns03, CursorBox, Edit01, FilterLines, Package, PackagePlus, Plus, SearchLg, Trash01, XClose } from '@untitled-ui/icons-react/build/cjs';
import { debounce, isEqual } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import BulkActionBar from 'src/components/BulkActionBar';
import ButtonCmp from 'src/components/ButtonCmp';
import CheckBox from 'src/components/CheckBox';
import CustomPagination from 'src/components/CustomPagination';
import DateRangePicker from 'src/components/DateRangePicker';
import DeleteModal from 'src/components/DeleteModal';
import DraggableLayout from 'src/components/DraggableLayout';
import InputText from 'src/components/InputText/InputText';
import NotFoundUI from 'src/components/NotFoundUI';
import PageSectionLayout from 'src/components/PageSectionLayout';
import Header from 'src/components/PageSectionLayout/Header/Header';
import SelectBox from 'src/components/SelectBox/SelectBox';
import TableCmp from 'src/components/TableCmp';
import FilterLayout from 'src/components/TableFilter/FilterLayout';
import TableTabButton from 'src/components/TableTabButton';
import CreateViewModal from 'src/components/TableTabButton/CreateViewModal';
import TooltipCmp from 'src/components/TooltipCmp';
import { EDIT_QUOTE_TABS, STATUS } from 'src/constants/common';
import { PATH } from 'src/constants/path';
import { setQuoteColumns } from 'src/redux/basic.slice';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { setAddressDetails, setDimensions, setIsShowQuoteDetailModal, setIsViewQuote, setActiveTabQuote, setServices, isShowQuoteDetailModalData } from 'src/redux/Quoting.slice';
import { RootState } from 'src/redux/store';
import { listQuote, getQuote, listQuoteCount, getQuoteViewList, createQuoteView, updateQuoteView, deleteQuoteView, updateTableColumnSettings } from 'src/services/QuoteService';
import { createDateWithoutTimezoneConversion } from 'src/utils/CommonFunctions';
import WalToast from 'src/utils/WalToast';

import { resetAddQuoteData, addQuoteIsModalOpen } from './AddQuote/AddQuote.slice';
import useDuplicateQuote from './AddQuote/useDuplicateQuote';
import QuoteCustomerLoaderRow from './Component/QuoteCustomerLoaderRow';
import QuoteCustomerRow from './Component/QuoteCustomerRow';
import QuoteDraggableCell from './Component/QuoteDraggableCell';
import { setCreateQuoteData } from './CreateQuote/CreateQuote.slice';
import AddQuoteBasicDetailsModal from './Modals/AddQuoteBasicDetailsModal';
import DatePickupModel from './Modals/DatePickupModel';
import FilterModal from './Modals/FilterModal';
import QuoteDetailModalNew from './Modals/QuoteDetailModalNew';
import SelectBillingAccount from './Modals/SelectBillingAccount';
import SelectDivision from './Modals/SelectDivision';
import { BULK_SELECTION_INITIAL_STATE, CP_CLASSIFICATION_OPTIONS, CP_QUOTE_TABLE_ALIAS, CP_STATUS_OPTION, DEFAULT_TAB_VALUES, MAX_VIEW_COUNT, SORTABLE_INITIAL_STATE } from './myQuote.const';
import { transformColumnsToHeadCells, isParamsChanged, formateTabData, extractViewNames, formatRowData, getActionColumnWidthClass } from './myQuote.helpers';
import { IQuoteTab, IRowSortable } from './myQuote.interface';
import {
    setQuoteParams,
    setActiveTab as setActiveTabRedux,
    setTabsData as setTabsDataRedux,
    setIsFetched,
    quoteParamsData,
    activeTabData,
    tabsDataData,
    isFetchedTabsData,
} from './MyQuoteDashboard.slice';
import QuoteBulkActionBar from './QuoteBulkActionBar';
import { setTeamMemberOptions } from './quotingDashboard.helpers';

const recordsPerPageArray = [
    { label: '10', value: 10 },
    { label: '25', value: 25 },
    { label: '50', value: 50 },
    { label: '100', value: 100 },
];

const initParams: any = {
    search: '',
    sortType: 'desc',
    sortField: 'updatedAt',
    page: 1,
    limit: 25,
    status: STATUS.ALL,
    member: 'all',
    startDate: null,
    endDate: null,
    classification: null,
};

// Status options with "All" for filter dropdowns
const STATUS_OPTION = [
    {
        label: 'All',
        value: STATUS.ALL,
    },
    ...CP_STATUS_OPTION,
];

const MyQuote = () => {
    const { t } = useTranslation();
    const { isTeamLoading, allTeamMemberListOptions } = useSelector((state: any) => state.basic);

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const uiState = useSelector((state: any) => state.MobileView);

    // Redux selectors - state persists across route changes
    const reduxQuoteParams = useAppSelector(quoteParamsData);
    const reduxActiveTab = useAppSelector(activeTabData);
    const reduxTabsData = useAppSelector(tabsDataData);
    const isFetchedTabs = useAppSelector(isFetchedTabsData);

    // AbortController refs for canceling in-flight requests
    const quoteAbortControllerRef = useRef<AbortController | null>(null);
    const quoteCountAbortControllerRef = useRef<AbortController | null>(null);
    const editQuoteAbortControllerRef = useRef<AbortController | null>(null);

    // Tab state - initialized from Redux
    const [tabsData, setTabsData] = useState<IQuoteTab[]>(reduxTabsData.length > 0 ? reduxTabsData : []);
    const [activeTab, setActiveTab] = useState<string>(reduxActiveTab);
    const [isTabsLoading, setIsTabsLoading] = useState(false);
    const [quoteCount, setQuoteCount] = useState<any[]>([]);

    // UI state
    const [isShowAction, setIsShowAction] = useState(false);
    const [isFilterView, setIsFilterView] = useState<any>({
        isShow: false,
        data: null,
    });

    // Date range state - initialized from Redux params
    const [selectedTimeRange, setSelectedTimeRange] = useState<string>('custom');
    const [isPrevYearDisable, setIsPrevYearDisable] = useState<any>(false);
    const [startDate, setStartDate] = useState<any>(reduxQuoteParams?.startDate ? moment(reduxQuoteParams.startDate).toDate() : null);
    const [endDate, setEndDate] = useState<any>(reduxQuoteParams?.endDate ? moment(reduxQuoteParams.endDate).toDate() : null);
    const [allTimeStartDate] = useState<any>('');

    // Modal state
    const [isShowSelectDivisionModal, setIsShowSelectDivisionModal] = useState(false);
    const [isShowSelectBillingAccountModal, setIsShowSelectBillingAccountModal] = useState(false);

    // Modal state for AddQuote flow
    const isShowAddQuoteBasicDetailsModal = useSelector(addQuoteIsModalOpen);

    // Params & data state - initialized from Redux
    const [params, setParams] = useState(reduxQuoteParams);
    const [isLoading, setIsLoading] = useState(true);
    const [search, setSearch] = useState(reduxQuoteParams?.search || '');
    const [quoteList, setQuoteList] = useState<any>([]);
    const [totals, setTotals] = useState(0);
    const [filterCount, setFilterCount] = useState<number>(0);
    const [searchApplied, setSearchApplied] = useState(false);
    const [showLoader, setShowLoader] = useState(false);
    const [selectedEditQuoteId, setSelectedEditQuoteId] = useState(null);
    const { duplicateQuote, duplicatingQuoteId } = useDuplicateQuote();
    const [selectedPickupDateQuoteId, setSelectedPickupDateQuoteId] = useState<number | null>(null);
    const [isShowListPickupDateModal, setIsShowListPickupDateModal] = useState(false);
    const isShowQuoteDetailModal = useAppSelector(isShowQuoteDetailModalData);
    const [isDisplayFilterModal, setIsDisplayFilterModal] = useState(false);
    const [teamMemberListOptions, setTeamMemberListOptions] = useState<any[]>([]);
    const [selectedUserOption, setSelectedUserOption] = useState<any>(null);
    const [searchTeamMember, setSearchTeamMember] = useState<string>('');
    const currentUser = useAppSelector((state: RootState) => state.auth.currentUser);

    // Custom views state
    const initAction = {
        create_view: false,
        delete_view: false,
        rename_view: false,
        data: null as any,
    };
    const [action, setAction] = useState<any>(initAction);
    const [viewNames, setViewNames] = useState<string[]>([]);
    const [isViewChangeLoading, setIsViewChangeLoading] = useState(false);
    const [isError, setIsError] = useState(false);

    // Column rearrangement state
    const [isRowSortable, setIsRowSortable] = useState<IRowSortable>(SORTABLE_INITIAL_STATE);
    const [columnsOrder, setColumnsOrder] = useState<any[]>([]);
    // Column settings are loaded at app boot via /common/init and stored in Redux (no per-page flash)
    const quoteColumns = useAppSelector((state: RootState) => state.basic.quoteColumns);

    const [bulkSelection, setBulkSelection] = useState(BULK_SELECTION_INITIAL_STATE);
    const selectedQuoteIds = bulkSelection.data;

    const [showAllSelected, setShowAllSelected] = useState(false);

    const displayedQuotes = useMemo(() => (showAllSelected ? quoteList.filter((q: any) => selectedQuoteIds.includes(q.id)) : quoteList), [showAllSelected, quoteList, selectedQuoteIds]);
    // Tailwind width class sized to the busiest row's action icons — applied to the action th and the row/skeleton action div so they match.
    const actionColumnWidthClass = useMemo(() => getActionColumnWidthClass(displayedQuotes), [displayedQuotes]);

    const setSelectedQuoteIds = useCallback((ids: number[]) => {
        if (ids.length === 0) setShowAllSelected(false);
        setBulkSelection({ isOpen: ids.length > 0, data: ids });
    }, []);

    const handleToggleSelectQuote = useCallback((id: number) => {
        setBulkSelection(({ data }) => {
            const next = data.includes(id) ? data.filter((i) => i !== id) : [...data, id];
            if (next.length === 0) setShowAllSelected(false);

            return { isOpen: next.length > 0, data: next };
        });
    }, []);

    const isSaveEnable = useMemo(
        () => isParamsChanged(isFilterView?.data, params),
        [params?.status, params?.classification, params?.search, params?.sortType, params?.sortField, params?.member, params?.startDate, params?.endDate, isFilterView?.data]
    );

    const isCreateViewEnable = useMemo(() => tabsData?.length < MAX_VIEW_COUNT, [tabsData?.length]);

    // Initialize default tabs — only when tabs haven't been fetched yet and local state is empty
    useEffect(() => {
        if (!isFetchedTabs && tabsData.length === 0) {
            setIsTabsLoading(true);
            const defaultTabs: IQuoteTab[] = [
                { value: 'All', name: 'All', count: 0 },
                { value: STATUS.OPENED, name: 'Requested', count: 0 },
                { value: STATUS.ACTIVE, name: t('Common.Quoted'), count: 0 },
                { value: STATUS.BOOKED, name: t('Common.Booked'), count: 0 },
            ];

            setTabsData(defaultTabs);
            dispatch(setTabsDataRedux(defaultTabs));
            setIsTabsLoading(false);
        }
    }, [isFetchedTabs, tabsData.length]);

    // Fetch custom saved views and merge with default tabs
    const getQuoteViewListData = async () => {
        setIsTabsLoading(true);
        const apiParams = { tableAlias: CP_QUOTE_TABLE_ALIAS };
        getQuoteViewList(apiParams)
            .then((response: any) => {
                dispatch(setIsFetched(true));
                const responseData = response?.data;

                if (responseData?.length > 0) {
                    const TabData = formateTabData(responseData);
                    const currentTabs = tabsData.length > 0 ? tabsData : [];
                    const mergedTabs = Array.from(new Map([...currentTabs, ...TabData].map((tab: IQuoteTab) => [tab.value, tab])).values());

                    setTabsData(mergedTabs);
                    dispatch(setTabsDataRedux(mergedTabs));
                    setViewNames(extractViewNames(mergedTabs));
                }

                // If the active tab is a custom view, sync params from the latest DB data
                // so that changes made in another browser are reflected immediately on load.
                if (!DEFAULT_TAB_VALUES.includes(activeTab)) {
                    const activeView = responseData?.find((view: any) => view.id?.toString() === activeTab);

                    if (activeView) {
                        const updatedParams = {
                            ...params,
                            page: 1,
                            search: activeView.appliedFilters?.search ?? '',
                            member: activeView.appliedFilters?.member ?? 'all',
                            startDate: activeView.appliedFilters?.startDate ?? null,
                            endDate: activeView.appliedFilters?.endDate ?? null,
                            sortType: activeView.appliedSorting?.sortType ?? params.sortType,
                            sortField: activeView.appliedSorting?.sortField ?? params.sortField,
                            status: activeView.appliedFilters?.status ?? 'All',
                            classification: activeView.appliedFilters?.classification ?? null,
                        };
                        setParams(updatedParams);
                        dispatch(setQuoteParams(updatedParams));
                        setSearch(activeView.appliedFilters?.search ?? '');
                        setStartDate(activeView.appliedFilters?.startDate ? moment(activeView.appliedFilters.startDate).toDate() : null);
                        setEndDate(activeView.appliedFilters?.endDate ? moment(activeView.appliedFilters.endDate).toDate() : null);
                    } else {
                        // Active custom view no longer exists (e.g. deleted in another browser) — fall back to 'All'
                        setActiveTab('All');
                        dispatch(setActiveTabRedux('All'));
                        setSearch('');
                        setStartDate(null);
                        setEndDate(null);
                        setParams({ ...initParams, status: 'All' });
                        dispatch(setQuoteParams({ ...initParams, status: 'All' }));
                    }
                }
            })
            .catch((error: any) => {
                console.log('getQuoteViewListData error', error);
            })
            .finally(() => {
                setIsTabsLoading(false);
            });
    };

    // Fetch custom tabs only after default tabs are initialized
    useEffect(() => {
        if (!isFetchedTabs && tabsData.length > 0) {
            getQuoteViewListData();
        }
    }, [tabsData.length, isFetchedTabs]);

    useEffect(() => {
        if (quoteCount.length > 0 && tabsData.length > 0) {
            const updatedTabs = tabsData.map((tab) => {
                const newCount = quoteCount.find((item: any) => item.value === tab.value);

                if (newCount) {
                    return { ...tab, count: newCount.count };
                }

                return tab;
            });

            const hasChanges = updatedTabs.some((tab, index) => tab.count !== tabsData[index]?.count);

            if (hasChanges) {
                setTabsData(updatedTabs);
                dispatch(setTabsDataRedux(updatedTabs));
            }
        }
    }, [quoteCount, tabsData.length]);

    // Fetch quote counts for tab badges
    const getQuoteCount = async () => {
        quoteCountAbortControllerRef.current?.abort();
        quoteCountAbortControllerRef.current = new AbortController();
        const signal = quoteCountAbortControllerRef.current.signal;

        // Use initParams (not Redux-persisted params) so tab counts always reflect
        // total unfiltered counts per status — user filters should not affect badge numbers
        const countParams = {
            ...initParams,
            status: 'All',
        };

        try {
            const response = (await listQuoteCount(countParams, signal)) as any;
            setQuoteCount(response?.counts ?? []);
        } catch (error: any) {
            if (error.code === 'ERR_CANCELED') return;
        }
    };

    // Fetch count on mount and when filter params change (not status/page/sort)
    useEffect(() => {
        getQuoteCount();

        return () => {
            quoteCountAbortControllerRef.current?.abort();
        };
    }, []);

    useEffect(() => {
        if (!isTeamLoading) {
            setTeamMemberListOptions(setTeamMemberOptions('all', params?.member, allTeamMemberListOptions));
        }
    }, [isTeamLoading]);

    useEffect(() => {
        if (allTeamMemberListOptions && allTeamMemberListOptions.length > 0) {
            setTeamMemberListOptions(setTeamMemberOptions('all', params?.member, allTeamMemberListOptions));
        }
    }, [allTeamMemberListOptions]);

    useEffect(() => {
        if (params?.member === 'all') {
            setTeamMemberListOptions(setTeamMemberOptions('all', params?.member, allTeamMemberListOptions));
        }

        if (params?.member && params?.member !== 'all') {
            const selectedMember = teamMemberListOptions.filter((user: any) => params?.member === user.value);

            setSelectedUserOption(selectedMember);
        } else {
            setSelectedUserOption(null);
        }
    }, [params?.member, allTeamMemberListOptions]);

    useEffect(() => {
        if (searchTeamMember) {
            const delayDebounceFn = setTimeout(() => {
                setTeamMemberListOptions(
                    setTeamMemberOptions(
                        'all',
                        params?.member,
                        allTeamMemberListOptions.filter((member: any) => `${member.firstName} ${member.lastName}`.toLowerCase().includes(searchTeamMember.toLowerCase()))
                    )
                );
            }, 250);

            return () => clearTimeout(delayDebounceFn);
        } else {
            setTeamMemberListOptions(setTeamMemberOptions('all', params?.member, allTeamMemberListOptions));
        }
    }, [searchTeamMember]);

    // Initialize isPrevYearDisable based on loaded dates
    useEffect(() => {
        if (startDate && endDate) {
            const twoYearsAgo = moment().subtract(1, 'years').startOf('year');
            const daysDiff = moment(endDate).diff(moment(startDate), 'days') + 1;
            const prevMonth = moment(startDate).subtract(daysDiff, 'day');

            if (prevMonth.isSameOrBefore(twoYearsAgo, 'days')) {
                setIsPrevYearDisable(true);
            } else {
                setIsPrevYearDisable(false);
            }
        }
    }, []);

    const handleDatePickerChange = (date: any, type: any) => {
        const [start, end] = date;
        setSelectedTimeRange(type);

        const twoYearsAgo = moment().subtract(1, 'years').startOf('year');
        const daysDiff = moment(end).diff(moment(start), 'days') + 1;
        const prevMonth = moment(start).subtract(daysDiff, 'day');

        if (prevMonth.isSameOrBefore(twoYearsAgo, 'days')) {
            setIsPrevYearDisable(true);
        } else {
            setIsPrevYearDisable(false);
        }

        const finalStartDate = start ?? null;
        const finalEndDate = end ? end ?? null : start ?? null;

        setStartDate(finalStartDate);
        setEndDate(finalEndDate);

        setParams((old: any) => ({
            ...old,
            page: 1,
            startDate: finalStartDate ? moment(finalStartDate).format('YYYY-MM-DD') : null,
            endDate: finalEndDate ? moment(finalEndDate).format('YYYY-MM-DD') : null,
        }));
    };

    const getFilterRange = (type?: string) => {
        var startDT = new Date();
        var endDT = new Date();
        const currentYear = new Date().getFullYear();
        const twoYearsAgo = moment().subtract(1, 'years').startOf('year');

        if (type === 'prev' && !isPrevYearDisable) {
            if (selectedTimeRange === 'yearly') {
                startDT = moment(startDate).subtract(1, 'year').toDate();
                endDT = moment(endDate).subtract(1, 'year').toDate();
                const prevMonth = moment(moment(startDate, 'YYYY')).subtract(1, 'year').format('YYYY');

                if (currentYear - parseInt(prevMonth) >= 1) {
                    setIsPrevYearDisable(true);
                } else {
                    setIsPrevYearDisable(false);
                }
            } else if (selectedTimeRange === 'weekly') {
                startDT = moment(endDate).subtract(1, 'week').isoWeekday(0).toDate();
                endDT = moment(endDate).subtract(1, 'week').isoWeekday(6).toDate();
                const prevMonth = moment(startDate).subtract(1, 'week');

                if (prevMonth.isSameOrBefore(twoYearsAgo, 'week')) {
                    setIsPrevYearDisable(true);
                } else {
                    setIsPrevYearDisable(false);
                }
            } else if (selectedTimeRange === 'monthly') {
                startDT = moment(startDate).subtract(1, 'months').startOf('month').toDate();
                endDT = moment(startDate).subtract(1, 'months').endOf('month').toDate();
                const prevMonth = moment(startDate).subtract(1, 'months');

                if (prevMonth.isSameOrBefore(twoYearsAgo, 'month')) {
                    setIsPrevYearDisable(true);
                } else {
                    setIsPrevYearDisable(false);
                }
            } else if (selectedTimeRange === 'daily') {
                startDT = moment(startDate).subtract(1, 'days').toDate();
                endDT = moment(endDate).subtract(1, 'days').toDate();
                const prevMonth = moment(startDate).subtract(1, 'day');

                if (prevMonth.isSameOrBefore(twoYearsAgo, 'day')) {
                    setIsPrevYearDisable(true);
                } else {
                    setIsPrevYearDisable(false);
                }
            } else {
                if (selectedTimeRange === 'quarterly') {
                    startDT = moment(moment(startDate).subtract(1, 'quarters').startOf('quarter'), 'YYYY-MM-DD').toDate();
                    endDT = moment(moment(startDate).subtract(1, 'quarters').endOf('quarter'), 'YYYY-MM-DD').toDate();
                    const prevMonth = moment(startDate).subtract(1, 'quarters');

                    if (prevMonth.isSameOrBefore(twoYearsAgo, 'quarters')) {
                        setIsPrevYearDisable(true);
                    } else {
                        setIsPrevYearDisable(false);
                    }
                } else if (selectedTimeRange === 'last_year') {
                    startDT = moment(startDate).subtract(1, 'year').toDate();
                    endDT = moment(startDate).subtract(1, 'year').toDate();
                } else {
                    const daysDiff = moment(endDate).diff(moment(startDate), 'days') + 1;
                    startDT = moment(startDate).clone().subtract(daysDiff, 'days').toDate();
                    endDT = moment(endDate).clone().subtract(daysDiff, 'days').toDate();
                    const daysDiffN = moment(endDT).diff(moment(startDT), 'days') + 1;
                    const prevMonth = moment(startDT).subtract(daysDiffN, 'day');

                    if (prevMonth.isSameOrBefore(twoYearsAgo, 'days')) {
                        setIsPrevYearDisable(true);
                    } else {
                        setIsPrevYearDisable(false);
                    }
                }
            }
            setStartDate(startDT);
            setEndDate(endDT);
            setParams((old: any) => ({
                ...old,
                page: 1,
                startDate: moment(startDT).format('YYYY-MM-DD'),
                endDate: moment(endDT).format('YYYY-MM-DD'),
            }));
        } else if (type === 'next') {
            setIsPrevYearDisable(false);

            if (selectedTimeRange === 'yearly') {
                startDT = moment(startDate).add(1, 'year').toDate();
                endDT = moment(endDate).add(1, 'year').toDate();
            } else if (selectedTimeRange === 'weekly') {
                startDT = moment(endDate).add(1, 'week').isoWeekday(0).toDate();
                endDT = moment(endDate).add(1, 'week').isoWeekday(6).toDate();
            } else if (selectedTimeRange === 'monthly') {
                startDT = moment(startDate).add(1, 'months').startOf('month').toDate();
                endDT = moment(endDate).add(1, 'months').endOf('month').toDate();
            } else if (selectedTimeRange === 'daily') {
                startDT = moment(startDate).add(1, 'days').toDate();
                endDT = moment(endDate).add(1, 'days').toDate();
            } else {
                if (selectedTimeRange === 'quarterly') {
                    startDT = moment(moment(startDate).add(1, 'quarters').startOf('quarter'), 'YYYY-MM-DD').toDate();
                    endDT = moment(moment(startDate).add(1, 'quarters').endOf('quarter'), 'YYYY-MM-DD').toDate();
                } else if (selectedTimeRange === 'last_year') {
                    startDT = moment(startDate).add(1, 'year').toDate();
                    endDT = moment(startDate).add(1, 'year').toDate();
                } else {
                    const daysDiff = moment(endDate).diff(moment(startDate), 'days') + 1;
                    startDT = moment(startDate).clone().add(daysDiff, 'days').toDate();
                    endDT = moment(endDate).clone().add(daysDiff, 'days').toDate();
                }
            }
            setStartDate(startDT);
            setEndDate(endDT);
            setParams((old: any) => ({
                ...old,
                page: 1,
                startDate: moment(startDT).format('YYYY-MM-DD'),
                endDate: moment(endDT).format('YYYY-MM-DD'),
            }));
        }
    };

    // Dynamic head cells using transformColumnsToHeadCells helper
    const headCellsQuoted = useMemo(() => {
        const hasData = quoteList.length > 0 && !isLoading;
        const result = transformColumnsToHeadCells(quoteColumns, hasData, t, actionColumnWidthClass);

        const checkboxCell = {
            id: 'checkbox',
            name: (
                <CheckBox
                    id="selectAllQuotes"
                    checked={false}
                    onChangeFunc={() => setSelectedQuoteIds(quoteList.map((q: any) => q.id))}
                    name="selectAllQuotes"
                    value="selectAllQuotes"
                    disabled={isLoading}
                />
            ),
            sortable: false,
            visible: true,
            rowClassName: 'w-[40px] min-w-[40px] max-w-[40px] !pr-2 sticky left-0 bg-inherit',
        };

        return [checkboxCell, ...result];
    }, [quoteList, isLoading, t, quoteColumns, setSelectedQuoteIds, actionColumnWidthClass]);

    // Fetch quote list with AbortController
    const fetchQuoteList = async () => {
        setIsLoading(true);
        setQuoteList([]);

        quoteAbortControllerRef.current?.abort();
        quoteAbortControllerRef.current = new AbortController();
        const signal = quoteAbortControllerRef.current.signal;

        try {
            const response = (await listQuote(params, signal)) as any;

            if (response?.data) {
                setQuoteList(
                    response.data.map((item: any) => ({
                        ...item,
                        fullName: `${item?.userData?.firstName}  ${item?.userData?.lastName}`,
                    }))
                );
            }

            const totalRecords = response?.pagination?.totalRecords ?? 0;
            setTotals(totalRecords);

            setIsLoading(false);
        } catch (e: any) {
            if (e.code === 'ERR_CANCELED') return;
            setIsLoading(false);
        }
    };

    // Team member list options come from /common/init (see init.thunks INIT_SECTIONS 'members')
    // and are read straight off redux above — this page no longer fetches them for itself.

    // Reset modal state when component mounts and unmounts
    useEffect(() => {
        dispatch(setIsShowQuoteDetailModal(false));

        return () => {
            dispatch(setIsShowQuoteDetailModal(false));
        };
    }, [dispatch]);

    // Fetch quotes when params change
    useEffect(() => {
        fetchQuoteList();

        return () => {
            quoteAbortControllerRef.current?.abort();
        };
    }, [params]);

    // Sync activeTab with params.status only when already on a default tab
    useEffect(() => {
        if (params.status && DEFAULT_TAB_VALUES.includes(params.status) && DEFAULT_TAB_VALUES.includes(activeTab) && params.status !== activeTab) {
            setActiveTab(params.status);
            dispatch(setActiveTabRedux(params.status));
        }
    }, [params.status]);

    // Persist params to Redux when they change
    useEffect(() => {
        dispatch(setQuoteParams(params));
    }, [params]);

    useEffect(() => {
        let fCount = 0;
        if (params?.startDate && params?.endDate) fCount += 1;
        if (params.member !== 'all' && params.member) fCount += 1;
        if (params.classification && params.classification !== 'all') fCount += 1;

        if (params.classification && params.classification !== 'all')
            if (uiState.isMobile || uiState.currentBreakpoint === 'SM') {
                if (params.status != 'All') fCount += 1;
            }

        setFilterCount(fCount);
    }, [params]);

    useEffect(() => {
        if (!isEqual(initParams, params)) {
            setSearchApplied(true);
        } else {
            setSearchApplied(false);
        }
    }, [params]);

    const searchDebounce = useCallback(
        debounce((debSearch: string) => setParams((old: any) => ({ ...old, search: debSearch, page: 1 })), 300),
        []
    );

    const handleFilter = (event: any) => {
        const { name, value } = event.target;

        const trimmedValue = value.trim().replace(/\s+/g, ' ');

        if (name === 'searchQuote') {
            setSearch(value);

            if (trimmedValue || value === '') {
                searchDebounce(trimmedValue);
            }
        }
    };

    const onClearFilter = () => {
        if (uiState.isMobile || uiState.currentBreakpoint === 'SM') {
            setParams(initParams);
            setSearch('');
            setStartDate(null);
            setEndDate(null);
            setSelectedTimeRange('custom');
        } else {
            setParams((old: any) => ({
                ...old,
                page: 1,
                startDate: null,
                endDate: null,
                member: 'all',
                classification: null,
            }));
            setStartDate(null);
            setEndDate(null);
            setSelectedTimeRange('custom');
        }

        setFilterCount(0);
    };

    const onNewQuote = () => {
        dispatch(resetAddQuoteData({ clearAll: false, openModal: true }));
    };

    const handleCloseSelectDivisionModal = () => {
        setIsShowSelectDivisionModal(false);
    };

    const handleDivisionSelected = (selectedDivisionId: number) => {
        const selectedDivision = currentUser?.divisions?.find((div: any) => div.id === selectedDivisionId);

        if (!selectedDivision) {
            navigate(PATH.CREATE_QUOTE);

            return;
        }

        const billingAccounts = selectedDivision?.customerBillingAccount || [];

        if (billingAccounts.length > 1) {
            setIsShowSelectBillingAccountModal(true);
        } else if (billingAccounts.length === 1) {
            dispatch(
                setCreateQuoteData({
                    data: { customerBillingAccountId: billingAccounts[0]?.id },
                })
            );
            navigate(PATH.CREATE_QUOTE);
        } else {
            navigate(PATH.CREATE_QUOTE);
        }
    };

    const handleCloseSelectBillingAccountModal = () => {
        setIsShowSelectBillingAccountModal(false);
    };

    const handlePagination = (page: number) => {
        setParams({ ...params, page });
    };

    const editQuote = async (quote: any) => {
        try {
            setShowLoader(true);
            editQuoteAbortControllerRef.current?.abort();
            editQuoteAbortControllerRef.current = new AbortController();

            setSelectedEditQuoteId(quote.id);

            if (quote && quote?.id) {
                const response = await getQuote(quote.id);

                if (response?.data && response?.status) {
                    const { dimensions, selectedServices, ...rest } = response?.data ?? {
                        dimensions: [],
                        selectedServices: [],
                    };

                    dispatch(setAddressDetails({ ...rest }));
                    dispatch(setDimensions(dimensions || []));
                    dispatch(setServices(selectedServices || []));
                    dispatch(setIsViewQuote(false));
                    dispatch(setActiveTabQuote(EDIT_QUOTE_TABS.SHIPPER_DETAILS));
                    dispatch(setIsShowQuoteDetailModal(true));
                } else {
                    const { dimensions, selectedServices, ...rest } = quote;

                    dispatch(setAddressDetails({ ...rest }));
                    dispatch(setDimensions(dimensions || []));
                    dispatch(setServices(selectedServices || []));
                    dispatch(setIsViewQuote(false));
                    dispatch(setActiveTabQuote(EDIT_QUOTE_TABS.SHIPPER_DETAILS));
                    dispatch(setIsShowQuoteDetailModal(true));
                }
            }
            setShowLoader(false);
        } catch (error: any) {
            console.log('editQuote error:', error);

            if (error.code !== 'ERR_CANCELED') {
                WalToast.error(error?.response?.data?.message ?? t('Common.Something_went_wrong'));
            }
            setShowLoader(false);
        }
    };

    // The quote list API already returns full Quote model attributes (no attribute whitelist
    // at the top level), so the row's own data has everything DatePickupModel needs — no fetch required.
    const handlePickupDateWarningClick = (quote: any) => {
        const { ...rest } = quote;

        setSelectedPickupDateQuoteId(quote.id);
        dispatch(setAddressDetails(rest));
        setIsShowListPickupDateModal(true);
    };

    const onRowClick = (quote: any) => () => {
        const currentTabData = tabsData.find((tab) => tab.value === activeTab);

        if (currentTabData?.appliedSorting) {
            dispatch(
                setQuoteParams({
                    ...initParams,
                    search: currentTabData?.appliedFilters?.search ?? '',
                    member: currentTabData?.appliedFilters?.member ?? 'all',
                    startDate: currentTabData?.appliedFilters?.startDate ?? null,
                    endDate: currentTabData?.appliedFilters?.endDate ?? null,
                    sortType: currentTabData?.appliedSorting?.sortType ?? initParams.sortType,
                    sortField: currentTabData?.appliedSorting?.sortField ?? initParams.sortField,
                    status: currentTabData?.appliedFilters?.status ?? 'All',
                    classification: currentTabData?.appliedFilters?.classification ?? null,
                })
            );
        } else {
            dispatch(setQuoteParams({ ...initParams, status: activeTab }));
        }

        navigate(`${PATH.MY_QUOTE}/${quote?.id}`, {
            state: {
                sourcePage: 'QuotingDashboard',
                ...quote,
            },
        });
    };

    const handleOutSideClick = () => {
        setIsShowAction(false);
    };

    // Custom views CRUD handlers
    const handleAction =
        (key: string, data: any = null) =>
        () => {
            setAction((old: any) => ({ ...old, [key]: true, data }));
        };

    const handleClose = useCallback(() => {
        setAction({
            create_view: false,
            delete_view: false,
            rename_view: false,
            data: null,
        });
    }, []);

    const handleUpdateView = async (data: any) => {
        setIsViewChangeLoading(true);
        updateQuoteView(data?.id, data)
            .then((response: any) => {
                if (response?.data) {
                    const responseData = response.data;
                    const tabIndex = tabsData.findIndex((tab: any) => tab.id === responseData?.id);

                    if (tabIndex !== -1) {
                        const updatedTabs = [...tabsData];
                        updatedTabs[tabIndex] = {
                            appliedFilters: responseData?.appliedFilters,
                            appliedSorting: responseData?.appliedSorting,
                            name: responseData?.name,
                            value: responseData?.id?.toString(),
                            count: response?.count ?? updatedTabs[tabIndex]?.count,
                            id: responseData?.id,
                            isIconShow: true,
                        };

                        setTabsData(updatedTabs);
                        dispatch(setTabsDataRedux(updatedTabs));
                        setViewNames(extractViewNames(updatedTabs));

                        if (!data?.rename) {
                            const updatedParams = {
                                ...params,
                                page: 1,
                                search: responseData?.appliedFilters?.search,
                                member: responseData?.appliedFilters?.member,
                                startDate: responseData?.appliedFilters?.startDate,
                                endDate: responseData?.appliedFilters?.endDate,
                                sortType: responseData?.appliedSorting?.sortType,
                                sortField: responseData?.appliedSorting?.sortField,
                                status: responseData?.appliedFilters?.status,
                                classification: responseData?.appliedFilters?.classification,
                            };
                            setParams(updatedParams);
                            dispatch(setQuoteParams(updatedParams));
                        }
                    }

                    if (!data?.rename) {
                        setIsFilterView({ isShow: false, data: null });
                    }
                    setIsShowAction(false);
                    handleClose();
                }
            })
            .catch(async (error: any) => {
                if (error?.response?.status === 422) {
                    setIsError(true);
                }
            })
            .finally(() => {
                setIsViewChangeLoading(false);
            });
    };

    const handleSave = (data: any) => {
        if (data?.rename) {
            const payload = {
                ...data,
                tableAlias: CP_QUOTE_TABLE_ALIAS,
            };
            handleUpdateView(payload);
        } else {
            setIsViewChangeLoading(true);
            const payload = {
                name: data.name,
                tableAlias: CP_QUOTE_TABLE_ALIAS,
                appliedFilters: {
                    search: params?.search || '',
                    member: params?.member || 'all',
                    startDate: params?.startDate || '',
                    endDate: params?.endDate || '',
                    status: params?.status || 'All',
                    classification: params?.classification || null,
                },
                appliedSorting: {
                    sortType: params?.sortType || 'desc',
                    sortField: params?.sortField || 'updatedAt',
                },
            };
            createQuoteView(payload)
                .then((response: any) => {
                    if (response?.data) {
                        const responseData = response.data;
                        const tabData: IQuoteTab = {
                            appliedFilters: responseData?.appliedFilters,
                            appliedSorting: responseData?.appliedSorting,
                            name: responseData?.name,
                            value: responseData?.id?.toString(),
                            count: response?.count ?? 0,
                            id: responseData?.id,
                            isIconShow: true,
                        };
                        const newTabs = [...tabsData, tabData];
                        setTabsData(newTabs);
                        dispatch(setTabsDataRedux(newTabs));
                        setViewNames((prev: any) => [...prev, responseData?.name]);

                        setActiveTab(tabData?.value);
                        dispatch(setActiveTabRedux(tabData?.value));

                        const updatedParams = {
                            ...params,
                            page: 1,
                            search: responseData?.appliedFilters?.search ?? params?.search,
                            member: responseData?.appliedFilters?.member ?? params?.member,
                            startDate: responseData?.appliedFilters?.startDate ?? params?.startDate,
                            endDate: responseData?.appliedFilters?.endDate ?? params?.endDate,
                            sortType: responseData?.appliedSorting?.sortType ?? params?.sortType,
                            sortField: responseData?.appliedSorting?.sortField ?? params?.sortField,
                            status: responseData?.appliedFilters?.status ?? params?.status,
                            classification: responseData?.appliedFilters?.classification ?? params?.classification,
                        };
                        setParams(updatedParams);
                        dispatch(setQuoteParams(updatedParams));

                        setIsFilterView({ isShow: false, data: null });
                        setIsShowAction(false);
                        handleClose();
                    }
                })
                .catch((error: any) => {
                    if (error?.response?.status === 422) {
                        setIsError(true);
                    }
                })
                .finally(() => {
                    setIsViewChangeLoading(false);
                });
        }
    };

    const handleViewSave = () => {
        if (isFilterView.data?.isIconShow) {
            // Editing existing custom view - save directly
            const payload = {
                id: isFilterView?.data?.id,
                name: isFilterView?.data?.name || '',
                tableAlias: CP_QUOTE_TABLE_ALIAS,
                appliedFilters: {
                    search: params?.search || '',
                    member: params?.member || 'all',
                    startDate: params?.startDate || '',
                    endDate: params?.endDate || '',
                    status: params?.status || 'All',
                    classification: params?.classification || null,
                },
                appliedSorting: {
                    sortType: params?.sortType || 'desc',
                    sortField: params?.sortField || 'updatedAt',
                },
            };
            handleUpdateView(payload);
        } else {
            // Default tab - open create view modal
            handleAction('create_view')();
        }
    };

    const handleFilterView = (isShow: boolean) => () => {
        const currentTabData = tabsData?.find((item) => item.value === activeTab);

        if (isShow) {
            const addParamsValue = {
                appliedFilters: {
                    status: params?.status,
                    classification: params?.classification,
                    search: params?.search,
                    member: params?.member,
                    startDate: params?.startDate,
                    endDate: params?.endDate,
                },
                appliedSorting: {
                    sortType: params?.sortType,
                    sortField: params?.sortField,
                },
            };
            const tabData = currentTabData?.isIconShow ? currentTabData : { ...currentTabData, ...addParamsValue };
            setIsFilterView({ isShow, data: tabData });
        } else {
            setIsFilterView({ isShow: false, data: null });
            setIsShowAction(false);

            if (isSaveEnable) {
                if (currentTabData?.isIconShow) {
                    setSearch(currentTabData?.appliedFilters?.search || '');
                    setStartDate(currentTabData?.appliedFilters?.startDate ? moment(currentTabData?.appliedFilters?.startDate).toDate() : null);
                    setEndDate(currentTabData?.appliedFilters?.endDate ? moment(currentTabData?.appliedFilters?.endDate).toDate() : null);

                    setParams((old: any) => ({
                        ...old,
                        search: currentTabData?.appliedFilters?.search,
                        member: currentTabData?.appliedFilters?.member || 'all',
                        startDate: currentTabData?.appliedFilters?.startDate || null,
                        endDate: currentTabData?.appliedFilters?.endDate || null,
                        sortType: currentTabData?.appliedSorting?.sortType,
                        sortField: currentTabData?.appliedSorting?.sortField,
                        status: currentTabData?.appliedFilters?.status,
                        classification: currentTabData?.appliedFilters?.classification,
                    }));
                } else {
                    setSearch('');
                    setStartDate(null);
                    setEndDate(null);

                    const originalTabValue = isFilterView?.data?.value;

                    if (originalTabValue) {
                        setActiveTab(originalTabValue);
                        dispatch(setActiveTabRedux(originalTabValue));
                    }

                    setParams({
                        ...initParams,
                        status: originalTabValue || currentTabData?.value,
                    });
                }
            }
        }
    };

    const handleRenameView = () => {
        const currentTab = tabsData.find((tab: IQuoteTab) => tab.value === activeTab);
        setAction({
            ...initAction,
            rename_view: true,
            data: { rename: true, tabName: currentTab?.name, id: currentTab?.id },
        });
    };

    const handleEditView = () => {
        handleFilterView(true)();
    };

    const handleDeleteView = () => {
        const currentTab = tabsData.find((tab: IQuoteTab) => tab.value === activeTab);
        setAction({
            ...initAction,
            delete_view: true,
            data: { view_name: currentTab?.name },
        });
    };

    const handleDeleteModal = useCallback(
        (status: boolean) => () => {
            if (status) {
                setIsViewChangeLoading(true);
                deleteQuoteView(Number(activeTab))
                    .then((response: any) => {
                        if (response?.data) {
                            const newTabs = tabsData.filter((tab: any) => tab.id !== response?.data?.id);
                            setTabsData(newTabs);
                            dispatch(setTabsDataRedux(newTabs));
                            setViewNames((prev: any) => prev.filter((name: any) => name !== response?.data?.name));

                            setActiveTab('All');
                            dispatch(setActiveTabRedux('All'));

                            setSearch('');
                            setStartDate(null);
                            setEndDate(null);
                            setParams({ ...initParams, status: 'All' });
                            dispatch(setQuoteParams({ ...initParams, status: 'All' }));

                            handleClose();
                        }
                    })
                    .catch(() => {
                        WalToast.error(t('Common.Something_went_wrong'));
                    })
                    .finally(() => {
                        setIsShowAction(false);
                        setIsViewChangeLoading(false);
                    });
            } else {
                handleClose();
            }
        },
        [activeTab, tabsData]
    );

    const VIEW_ACTIONS = useMemo(
        () => [
            {
                icon: Edit01,
                label: t('QuotingHub.QuotingDashboard.Rename_View'),
                onClick: handleRenameView,
            },
            {
                icon: CursorBox,
                label: t('QuotingHub.QuotingDashboard.Edit_View'),
                onClick: handleEditView,
            },
            {
                icon: Trash01,
                label: t('QuotingHub.QuotingDashboard.Delete_View'),
                onClick: handleDeleteView,
                isDestructive: true,
            },
        ],
        [activeTab, tabsData]
    );

    const MODEL_TEXT = useMemo(() => {
        let title = t('QuotingHub.QuotingDashboard.Create_New_View');
        let primaryBtnText = t('Common.Save');

        if (action.rename_view) {
            title = t('QuotingHub.QuotingDashboard.Rename_View');
        } else {
            title = t('QuotingHub.QuotingDashboard.Create_New_View');
            primaryBtnText = t('QuotingHub.QuotingDashboard.Create_View');
        }

        return { primaryBtnText, title };
    }, [action]);

    // Column rearrangement handlers
    const handleSortable =
        (isOpen: boolean = true) =>
        () => {
            if (isOpen) {
                const formattedData = formatRowData(quoteList.length ? quoteList : []);

                if (formattedData.length > 0) {
                    // Order draggable data according to current column order
                    const sorted = quoteColumns
                        .map((column: any) => {
                            const foundColumn = formattedData[0].find((f: any) => f.id === column.id);

                            return foundColumn ? { ...foundColumn, visible: column.visible } : null;
                        })
                        .filter(Boolean);
                    setIsRowSortable({
                        isOpen: true,
                        data: [sorted],
                    });
                }
            } else {
                setIsRowSortable(SORTABLE_INITIAL_STATE);
                setColumnsOrder([]);
            }
        };

    const handleColumnChange = (columns: any) => {
        setColumnsOrder(columns);
    };

    const hasColumnChanges = useMemo(() => {
        if (!columnsOrder || columnsOrder.length === 0) {
            return false;
        }

        return columnsOrder.some((col: any) => {
            const originalCol = quoteColumns.find((qCol: any) => qCol.id === col.id);

            if (!originalCol) return true;

            return col.displayOrder !== originalCol.displayOrder || col.visible !== originalCol.visible;
        });
    }, [columnsOrder, quoteColumns]);

    const handleColumnOrderSubmit = async () => {
        const cleanedColumns = columnsOrder.map((column: any) => {
            // eslint-disable-next-line no-unused-vars
            const { data: data, boxWrapClassName: boxWrapClassName, ...rest } = column;

            return rest;
        });
        dispatch(setQuoteColumns(cleanedColumns));
        setIsRowSortable(SORTABLE_INITIAL_STATE);

        // Persist to API
        updateTableColumnSettings({
            tableAlias: CP_QUOTE_TABLE_ALIAS,
            columnSettings: cleanedColumns,
        }).catch(() => {
            // Silent fail - Redux state already updated
        });
    };

    const handleTabClick = (tab: any) => () => {
        if (tab?.value === activeTab) {
            if (tab?.isIconShow) {
                setIsShowAction((old: any) => !old);
            }

            return;
        } else {
            setIsShowAction(false);

            if (tab?.appliedSorting) {
                // Custom view tab - restore its saved filters
                setSearch(tab?.appliedFilters?.search || '');
                setStartDate(tab?.appliedFilters?.startDate ? moment(tab?.appliedFilters?.startDate).toDate() : null);
                setEndDate(tab?.appliedFilters?.endDate ? moment(tab?.appliedFilters?.endDate).toDate() : null);

                const updatedParams = {
                    ...params,
                    page: 1,
                    search: tab?.appliedFilters?.search,
                    member: tab?.appliedFilters?.member || 'all',
                    startDate: tab?.appliedFilters?.startDate || null,
                    endDate: tab?.appliedFilters?.endDate || null,
                    sortType: tab?.appliedSorting?.sortType,
                    sortField: tab?.appliedSorting?.sortField,
                    status: tab?.appliedFilters?.status,
                    classification: tab?.appliedFilters?.classification,
                };
                setParams(updatedParams);
                dispatch(setQuoteParams(updatedParams));
            } else {
                // Default tab - reset to clean state with status filter
                setSearch('');
                setStartDate(null);
                setEndDate(null);

                const updatedParams = {
                    ...initParams,
                    status: tab.value,
                };
                setParams(updatedParams);
                dispatch(setQuoteParams(updatedParams));
            }

            setActiveTab(tab?.value);
            dispatch(setActiveTabRedux(tab?.value));
        }
    };

    const handleStatusClassification = (field: string) => (event: any) => {
        const newValue = event?.value ?? (field === 'status' ? 'All' : null);

        setParams((old: any) => ({
            ...old,
            page: 1,
            [field]: newValue,
        }));

        if (field === 'status' && DEFAULT_TAB_VALUES.includes(activeTab)) {
            setActiveTab(newValue);
            dispatch(setActiveTabRedux(newValue));
        }
    };

    return (
        <>
            <PageSectionLayout
                header={
                    <Header
                        title={t('QuotingHub.My_Quotes')}
                        Icon={PackagePlus}
                        mainContainerClassName="flex-nowrap !flex-row "
                        leftClassName="!w-auto"
                        rightClassName="!w-auto"
                        isShowNotificationBox
                        rightSideContent={
                            <>
                                <ButtonCmp type="button" className="btn-outline-primary" icon={<Plus className="w-4 h-4" />} onClick={onNewQuote}>
                                    {t('QuotingHub.Add_New_Quote')}
                                </ButtonCmp>
                            </>
                        }
                    />
                }
            >
                <div className="table-box-wrap w-full relative overflow-hidden">
                    <div className={`transition-all duration-300  ${isFilterView.isShow ? 'h-[90px]' : 'h-[45px]'}`}>
                        {isFilterView.isShow ? (
                            <FilterLayout
                                isSaveBtn={DEFAULT_TAB_VALUES?.includes(activeTab) ? isCreateViewEnable : true}
                                onSave={handleViewSave}
                                onCancel={handleFilterView(false)}
                                search={search}
                                handleSearchChange={handleFilter}
                                isSaveEnable={isSaveEnable}
                                isLoading={isViewChangeLoading}
                            >
                                <>
                                    <SelectBox
                                        parentClassName={`filter-view-selectbox filter-view-selectbox-custom ${
                                            params?.status !== 'All' ? 'filter-selectbox-with-value' : 'filter-selectbox-with-no-value'
                                        }`}
                                        SelectDropdownWrapClass="filter-select-dropdown-sm"
                                        name="status"
                                        required
                                        className={`form_control`}
                                        options={STATUS_OPTION}
                                        placeholder={t('Common.Status')}
                                        isClearable={params?.status?.trim()?.toLowerCase() === 'all' ? false : true}
                                        value={params.status ? STATUS_OPTION?.filter((val: any) => params.status === val?.value) : 'All'}
                                        onChangeFunc={handleStatusClassification('status')}
                                    />
                                    <SelectBox
                                        parentClassName={`filter-view-selectbox filter-view-selectbox-custom ${
                                            params?.classification ? 'filter-selectbox-with-value' : 'filter-selectbox-with-no-value'
                                        }`}
                                        name="classification"
                                        className={`form_control`}
                                        options={CP_CLASSIFICATION_OPTIONS}
                                        placeholder={t('Common.Type')}
                                        isClearable={true}
                                        value={params.classification ? CP_CLASSIFICATION_OPTIONS?.filter((val: any) => params.classification === val?.value) : 'All'}
                                        onChangeFunc={handleStatusClassification('classification')}
                                    />
                                    <SelectBox
                                        parentClassName={`filter-view-selectbox filter-view-selectbox-custom ${
                                            params?.member !== 'all' ? 'w-fit filter-selectbox-with-value' : ' w-[92px] filter-selectbox-with-no-value'
                                        }`}
                                        name="userListGroup"
                                        required
                                        className={`form_control`}
                                        labelClassName="form_label mb-1.5 block"
                                        SelectDropdownWrapClass="filter-select-dropdown-md"
                                        options={teamMemberListOptions}
                                        placeholder={t('Common.User')}
                                        isClearable={true}
                                        isSearchable={true}
                                        value={selectedUserOption}
                                        noOptionMessage={t('QuotingHub.QuotingAnalytics.No_Users_Found')}
                                        onChangeFunc={(event: any, actionMeta: any) => {
                                            setParams((old: any) => ({
                                                ...old,
                                                page: 1,
                                                member: event?.value ?? 'all',
                                            }));

                                            if (actionMeta?.action === 'clear') {
                                                setSearchTeamMember('');
                                            }
                                        }}
                                        onInputChange={(e: any, { action: actionData }: any) => {
                                            if (actionData === 'input-change' || actionData === 'input-blur') {
                                                setSearchTeamMember(e);
                                            }
                                        }}
                                    />
                                    <div className="w-fit">
                                        <DateRangePicker
                                            minDate={moment().subtract(1, 'years').startOf('year').toDate()}
                                            maxDate={createDateWithoutTimezoneConversion(moment())}
                                            handleDatePickerChange={(dates: any, type?: string) => handleDatePickerChange(dates, type)}
                                            dateRangeWrapClass={`bg-white !rounded-md !h-[28px] ${params?.startDate && params?.endDate ? '' : '!border-dashed'}`}
                                            selectedTimeRange={selectedTimeRange}
                                            startDate={startDate}
                                            endDate={endDate}
                                            allTimeStartDate={allTimeStartDate}
                                            showYearDropdown={false}
                                            isShowDropDownIcon={false}
                                            isPrevDisabled={isPrevYearDisable}
                                            onPrevNextClick={(type: string) => getFilterRange(type)}
                                            isClearable={true}
                                            containerClassName="xls:left-0 xls:right-[unset] lg:left-[unset] lg:right-0 left-0"
                                            parentClassName=""
                                            textWrapClass="!justify-start flex-row-reverse !gap-x-1.5"
                                            calendarClass="!mr-0"
                                            placeholder={'Date'}
                                            placeholderClassname="!font-semibold !text-textSecondary "
                                        />
                                    </div>
                                </>
                            </FilterLayout>
                        ) : (
                            <div className="table-bottom-header table-header-filter-space !gap-x-2 items-center !border-b border-utilityGray200 !flex-nowrap">
                                <div className={`table-header-bottom-left flex gap-x-1 flex-1 scrollbar-hide ${isRowSortable.isOpen ? 'opacity-60 pointer-events-none !cursor-default' : ''}`}>
                                    <div className="flex items-center gap-x-1">
                                        <TableTabButton
                                            tabArray={tabsData}
                                            tabParentClassName="whitespace-nowrap scrollbar-hide"
                                            isActive={activeTab}
                                            isTabDataInClick={true}
                                            isShowAction={isShowAction}
                                            tabTextClass="!max-w-[104px] truncate"
                                            handleOnClick={handleTabClick}
                                            handleOutSideClick={handleOutSideClick}
                                            isSkeletonLoading={isTabsLoading}
                                            skeletonCount={3}
                                            viewActions={VIEW_ACTIONS}
                                        />
                                        {isCreateViewEnable && !isTabsLoading && (
                                            <div
                                                className="cursor-pointer p-[5px] text-textSecondary text-xs leading-[1.5] font-semibold rounded-lg hover:bg-utilityGray100"
                                                onClick={handleAction('create_view')}
                                            >
                                                <TooltipCmp message={t('QuotingHub.QuotingDashboard.Create_View')}>
                                                    <Plus className="w-4 h-4 flex-none" />
                                                </TooltipCmp>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {!isRowSortable.isOpen ? (
                                    <>
                                        <ButtonCmp className="btn_secondary_black flex-none !px-2 !py-[5px] !gap-1" onClick={handleFilterView(true)}>
                                            <SearchLg className="w-4 h-4" />
                                            <FilterLines className="w-4 h-4" />
                                        </ButtonCmp>
                                        <ButtonCmp
                                            className="btn_secondary_black flex-none"
                                            onClick={handleSortable()}
                                            size="square-sm"
                                            disabled={quoteList.length === 0}
                                            icon={<Columns03 className="w-4 h-4" />}
                                        >
                                            <></>
                                        </ButtonCmp>
                                        <InputText
                                            inputName="searchQuote"
                                            placeholder={t('Common.Search')}
                                            className="bg-white focus:bg-white pl-8 pr-7 placeholder:text-gray500 shadow-sm font-normal search-input "
                                            icon={<SearchLg className="absolute top-1/2 -translate-y-1/2 left-2 text-grayText h-4 w-4" />}
                                            value={search}
                                            inputType="text"
                                            onChangeFunc={handleFilter}
                                            parentClassName="table-searchInput max-mdm:flex-1 max-smd:flex-[0_0_100%] hidden flex-none"
                                            isClearable={true}
                                        />
                                        <ButtonCmp
                                            type="submit"
                                            className={`btn_secondary_black table-filter-btn hidden ${filterCount > 0 ? 'border-borderSecondary hover:border-borderSecondary' : ''}`}
                                            onClick={(e: any) => {
                                                if (e?.target?.id !== 'closeBtn' && e?.target?.parentElement?.id !== 'closeBtn') setIsDisplayFilterModal(true);
                                            }}
                                            icon={<FilterLines className=" w-4 h-4 " />}
                                        >
                                            Filters
                                            {filterCount > 0 ? (
                                                <>
                                                    <p className="filter-btn-selected">{filterCount}</p>
                                                    <XClose
                                                        className="w-4 h-4 text-primary700"
                                                        id="closeBtn"
                                                        onClick={(e: any) => {
                                                            e.preventDefault();
                                                            onClearFilter();
                                                        }}
                                                    />
                                                </>
                                            ) : (
                                                ''
                                            )}
                                        </ButtonCmp>
                                        <div className="table-recordsPerPage flex-none">
                                            <SelectBox
                                                SelectDropdownWrapClass="table-recordsPerPageDropdown"
                                                selectLabelWrapClass="table-recordsPerPageSelectValue"
                                                name="recordsPerPageGroup"
                                                id="recordsPerPageGroup"
                                                className="form_control shadow"
                                                size="sm"
                                                options={recordsPerPageArray}
                                                onChangeFunc={(event: any) => {
                                                    setParams((old: any) => {
                                                        if (old.limit === event.value) {
                                                            return old;
                                                        }

                                                        return {
                                                            ...old,
                                                            limit: event.value,
                                                            page: 1,
                                                        };
                                                    });
                                                }}
                                                isSearchable={false}
                                                value={recordsPerPageArray.find((val: any) => val.value === params.limit)}
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <ButtonCmp type="button" onClick={handleSortable(false)} className="btn_secondary_black">
                                            {t('Common.Cancel')}
                                        </ButtonCmp>
                                        <ButtonCmp type="button" onClick={handleColumnOrderSubmit} disabled={!hasColumnChanges} className="btn-outline-primary">
                                            {t('Common.Save')}
                                        </ButtonCmp>
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    <div className={`draggable-layout-view ${isRowSortable.isOpen ? 'slide-in' : 'slide-out'}`}>
                        <DraggableLayout sortableData={isRowSortable?.data} renderCellContent={QuoteDraggableCell} onColumnChange={handleColumnChange} />
                    </div>

                    <div data-test-id="userTest" className={`w-full table-box-container draggable-layout-view ${isRowSortable.isOpen ? 'slide-out pointer-events-none' : 'slide-in'}`}>
                        {bulkSelection.isOpen && (
                            <BulkActionBar
                                selectedRows={selectedQuoteIds}
                                setSelectedRows={setSelectedQuoteIds}
                                allIds={quoteList.map((q: any) => q.id)}
                                showOnlySelected={showAllSelected}
                                onShowOnlySelectedChange={setShowAllSelected}
                                toolbar={<QuoteBulkActionBar />}
                            />
                        )}

                        <div className="overflow-x-auto custom-scrollbar scrollbar-hide">
                            <TableCmp
                                headCells={headCellsQuoted}
                                tableDataArr={displayedQuotes}
                                TableLoaderRowCmp={QuoteCustomerLoaderRow}
                                isBulkBarOpen={bulkSelection.isOpen}
                                TableRowCmp={QuoteCustomerRow}
                                tableRowCmpProps={{
                                    onRowClick: onRowClick,
                                    showLoader,
                                    editQuote,
                                    selectedEditQuoteId,
                                    duplicateQuote,
                                    selectedDuplicateQuoteId: duplicatingQuoteId,
                                    onPickupDateWarningClick: handlePickupDateWarningClick,
                                    headCells: headCellsQuoted,
                                    selectedQuoteIds,
                                    onToggleSelect: handleToggleSelectQuote,
                                    actionColumnWidthClass,
                                }}
                                tableBodyRowClass="bg-white"
                                params={params}
                                setParams={setParams}
                                tableSortingOnClick={() => setIsLoading(true)}
                                tableRowHeight="xs"
                                isTableDataLoading={isLoading}
                                numberOfSkeletonRows={20}
                                isTableRowClickable={true}
                                isFirstColumnSticky
                                isActionColumnSticky={actionColumnWidthClass !== '!p-0'}
                            />
                        </div>
                        {quoteList.length <= 0 && !isLoading && (
                            <NotFoundUI
                                handleActionType={onNewQuote}
                                buttonText={searchApplied ? '' : t('QuotingHub.Add_New_Quote')}
                                title={searchApplied ? t('QuotingHub.No_Quotes_Found') : t('QuotingHub.No_Quotes_Added')}
                                desc={searchApplied ? t('QuotingHub.No_Quotes_Found_desc') : t('QuotingHub.No_Quotes_Added_desc')}
                                containerClassName="min-h-[calc(100%_-_172.5px)] !h-auto"
                                testId="wal-QuotingDashboard-dataNotFoundText"
                                iconClassName="!hidden"
                                iconSecondaryClassName="!flex"
                                contentClassName="before:!bg-none"
                                iconSecondary={searchApplied ? <SearchLg className="w-5 h-5 text-primary" /> : <Package className="w-5 h-5 text-primary" />}
                            />
                        )}
                    </div>

                    {!isRowSortable.isOpen && (
                        <div className="w-full bg-white rounded-b-xl">
                            <CustomPagination recordsPerPage={params.limit} totalRecords={totals} currentPage={params.page} handlePagination={handlePagination} />
                        </div>
                    )}
                </div>
            </PageSectionLayout>

            {isShowQuoteDetailModal && (
                <QuoteDetailModalNew
                    handleUpdate={(updatedData: any) => {
                        if (updatedData?.quote) {
                            const newDimensions = updatedData.dimensions || [];

                            const weightMeasurement = newDimensions[0]?.weightMeasurement ?? null;

                            setQuoteList((prev: any[]) =>
                                prev.map((q: any) => {
                                    if (q.id !== updatedData.quote.id) return q;

                                    return {
                                        ...q,
                                        ...updatedData.quote,
                                        userData: q.userData,
                                        adminData: q.adminData,
                                        weightMeasurement,
                                        loadDetails: updatedData.loadDetails ?? q.loadDetails,
                                    };
                                })
                            );
                        }
                    }}
                />
            )}

            {isShowListPickupDateModal && (
                <DatePickupModel
                    handleClose={() => {
                        setIsShowListPickupDateModal(false);
                        setSelectedPickupDateQuoteId(null);
                    }}
                    onUpdate={(pickupDate, deliveryDate, pickupEndDate) => {
                        setQuoteList((prev: any[]) =>
                            prev.map((q: any) => (q.id === selectedPickupDateQuoteId ? { ...q, pickupDate, deliveryDate: deliveryDate ?? q.deliveryDate, pickupEndDate } : q))
                        );
                    }}
                />
            )}

            {isDisplayFilterModal && (
                <FilterModal handleClose={() => setIsDisplayFilterModal(false)} params={params} setFilterCount={setFilterCount} setParams={setParams} statusArr={CP_STATUS_OPTION} />
            )}

            {isShowSelectDivisionModal && <SelectDivision handleClose={handleCloseSelectDivisionModal} onDivisionSelected={handleDivisionSelected} />}

            {isShowSelectBillingAccountModal && <SelectBillingAccount handleClose={handleCloseSelectBillingAccountModal} />}

            {isShowAddQuoteBasicDetailsModal && <AddQuoteBasicDetailsModal handleClose={() => dispatch(resetAddQuoteData({ clearAll: false, openModal: false }))} />}

            {(action.create_view || action.rename_view) && (
                <CreateViewModal
                    title={MODEL_TEXT.title}
                    primaryBtnText={MODEL_TEXT.primaryBtnText}
                    handleClose={handleClose}
                    handleSave={handleSave}
                    isLoading={isViewChangeLoading}
                    viewNames={viewNames}
                    modalData={action?.data}
                    isError={isError}
                    setIsError={setIsError}
                />
            )}

            {action.delete_view && (
                <DeleteModal
                    moduleTitle={t('QuotingHub.QuotingDashboard.Delete_View')}
                    moduleDeleteModalContent={t('QuotingHub.QuotingDashboard.Delete_View_desc', {
                        View_name: action?.data?.view_name,
                    })}
                    handleClose={handleDeleteModal}
                    dangerBtnText={t('Common.Delete')}
                    isDeleteLoading={isViewChangeLoading}
                />
            )}
        </>
    );
};

export default MyQuote;
